import { useCallback, useEffect, useMemo, useState } from 'react'
import { FoodsManager, LastAndFood } from '../../../../hooks/useFood'
import { Food } from '../../../../core/db_food'
import { exportFoodsToFile, getAllFoodsAsMap, updateFoodsIds } from './coreFoodsExport'
import { dbManager, ILastFood } from '../../../../core/db'
import { curTime } from '../../../../core/helper'
import { LastFood } from '../../../../core/db_lastfood'


export type SelectedFoodItemsInfo = {
  isSelected: boolean
  isSelectChildren: boolean
}
export const useSelectAndExport = (foodsManager: FoodsManager) => {
    const manager = useMemo(() => new SelectedFoods(foodsManager), [foodsManager])


    const [info, setInfo] = useState<SelectedFoodItemsInfo>({ isSelected: false, isSelectChildren: true })
    const [selectedFoods, setSelectedFoods] = useState({} as Record<number, Food>)
    const [selectedFoodsCount, setSelectedFoodsCount] = useState({} as Record<number, number>)
    const selectFoodItem = useCallback((laf: LastAndFood) => {
        manager.selectFood(laf.f, info.isSelectChildren).then(() => {
            setSelectedFoods(manager.getFoods())
            setSelectedFoodsCount(manager.getFoodsCount())
        })
    }, [manager, info.isSelectChildren])
    const unselectFoodItem = useCallback((laf: LastAndFood) => {
        manager.unselectFood(laf.f, info.isSelectChildren).then(() => {
            setSelectedFoods(manager.getFoods())
            setSelectedFoodsCount(manager.getFoodsCount())
        })
    }, [manager, info.isSelectChildren])
    const selectAllFoods = useCallback(() => {
        manager.selectAllFoods().then(() => {
            setSelectedFoods(manager.getFoods())
            setSelectedFoodsCount(manager.getFoodsCount())
        })
    }, [manager, setSelectedFoods])
    useEffect(() => {
        if (info.isSelected !== manager.hasFoods) {
            setInfo({ ...info, isSelected: manager.hasFoods })
        }
    }, [manager.hasFoods, info.isSelected])
    const exportProducts = useCallback(async (tag: string, exportPassword?: string) => {
        // сначала заменяем все используемые id продуктов на новые
        // id это у нас dt поле (нужно убедиться что обновлены и dt и ингредиентов)
        await exportFoodsToFile(Object.values(selectedFoods), 1, tag, exportPassword)
    }, [selectedFoods])
    const importProducts = useCallback(async (foods: Food[]) => {
        // console.log('importProducts into database', foods)
        const maxDt = (await dbManager.db.foods.orderBy('dt').last())?.dt || 0
        // console.log('maxDt', maxDt)
        const t = getAllFoodsAsMap(foods)
        const countT = t.size
        // console.log('countT', countT)

        const StartId = curTime(true) - countT > maxDt
            ? curTime(true) - countT
            : maxDt + 1
        // const StartId = curTime(true)
        const [newFoods, newStartId] = updateFoodsIds(foods, StartId)
        await dbManager.db.foods.bulkPut(newFoods)

        for (const food of newFoods.slice(0, 20)) {
            const lastFood: LastFood & ILastFood = {
                dt: curTime(true) + 1,
                fdt: food.dt,
                g: food.lw,
                pt: 1,
            }
            await dbManager.db.lastFoods.add(lastFood)
        }

        if (newStartId > curTime(true)) {
            const timeDiff = newStartId - curTime(true)
            const timeDiffStr = timeDiff > 60 ? `${Math.floor(timeDiff / 60)} минут` : `${timeDiff} секунд`
            // eslint-disable-next-line max-len
            const msg = `В связи с спецификой импорта продуктов, следующий продукт можно будет добавить только через ${timeDiffStr}, а точнее, в ${new Date(newStartId * 1000).toLocaleString()}.

Данный момент требуется отслеживать самостоятельно`
            // console.error('newStartId is greater than current time', newStartId, curTime(true))
            // console.error(msg)
            alert(msg)
        }
        // console.log('newFoods', newFoods)
    }, [])
    const unselectAllFoods = useCallback(() => {
        manager.unselectAllFoods()
        setSelectedFoods(manager.getFoods())
        setSelectedFoodsCount(manager.getFoodsCount())
    }, [manager])
    return {
        selectedFoodsInfo: info,
        setSelectedFoodsInfo: setInfo,
        selectedFoods,
        selectAllFoods,
        selectFoodItem,
        unselectFoodItem,
        unselectAllFoods,
        exportProducts,
        importProducts,
    }
}

class SelectedFoods {
    private foods: Record<number, Food>
    private foodsCount: Record<number, number>
    private foodsManager: FoodsManager
    private _hasFoods: boolean = false
    constructor(foodsManager: FoodsManager) {
        this.foodsManager = foodsManager
        this.foods = {}
        this.foodsCount = {}
    }
    public async selectAllFoods() {
        const foods = await this.foodsManager.getAllFoods()
        for (let i = 0; i < foods.length; i++) {
            await this._selectFood(foods[i], true, [])
            if (i % 100 == 0) {
                console.log('selected', i, 'of', foods.length, 'foods')
            }
        }
        this.foods = { ...this.foods }
        this.foodsCount = { ...this.foodsCount }
        this._hasFoods = true
    }
    public async selectFood(food: Food, withChildren: boolean) {
        await this._selectFood(food, withChildren, [])
        this.foods = { ...this.foods }
        this.foodsCount = { ...this.foodsCount }
        this._hasFoods = true
    }
    public async unselectFood(food: Food, withChildren: boolean) {
        await this._unselectFood(food, withChildren, [])
        delete this.foodsCount[food.dt]
        delete this.foods[food.dt]
        this.foods = { ...this.foods }
        this.foodsCount = { ...this.foodsCount }
        if (Object.keys(this.foodsCount).length === 0) {
            this._hasFoods = false
        }
    }
    public unselectAllFoods() {
        this.foods = {}
        this.foodsCount = {}
        this._hasFoods = true
    }
    public getFoods() {
        return this.foods
    }
    public getFoodsCount() {
        return this.foodsCount
    }
    public get hasFoods(): boolean {
        return this._hasFoods
    }

    private async _selectFood(food: Food, withChildren: boolean, handledIds: number[]) {
        if (handledIds.length > 200) {
            return
        }
        if (handledIds.includes(food.dt)) {
            return
        }
        handledIds.push(food.dt)
        if (food.dt in this.foodsCount) {
            this.foodsCount[food.dt] += 1
        } else {
            this.foodsCount[food.dt] = 1
        }
        if (withChildren) {
            this.foods[food.dt] = food
        } else {
            this.foods[food.dt] = {
                ...food,
                m: false,
                mc: [],
            }
        }
        await this.selectFoodWithChildren(food, withChildren, handledIds)

    }
    private async _unselectFood(food: Food, withChildren: boolean, handledIds: number[]) {
        if (handledIds.length > 200) {
            return
        }
        if (handledIds.includes(food.dt)) {
            return
        }
        handledIds.push(food.dt)
        if (food.dt in this.foodsCount) {
            this.foodsCount[food.dt] -= 1
        }
        if (this.foodsCount[food.dt] === 0) {
            delete this.foodsCount[food.dt]
            delete this.foods[food.dt]
        }
        await this.unselectFoodWithChildren(food, withChildren, handledIds)
    }
    private async selectFoodWithChildren(food: Food, withChildren: boolean, handledIds: number[]) {
        if (!food.m || !withChildren) {
            return
        }
        for (const ingredient of food.mc) {
            const ingredientFood = await this.foodsManager.getFoods([ingredient.dt])
            for (const one of ingredientFood) {
                await this._selectFood(one, true, handledIds)
            }
        }
    }
    private async unselectFoodWithChildren(food: Food, withChildren: boolean, handledIds: number[]) {
        if (!food.m || !withChildren) {
            return
        }
        for (const ingredient of food.mc) {
            const ingredientFood = await this.foodsManager.getFoods([ingredient.dt])
            for (const one of ingredientFood) {
                await this._unselectFood(one, true, handledIds)
            }
        }
    }
}